import { listen, qs, qsa, toggle } from "@fluorescent/dom";
import accordionsInit from "@/lib/accordions";
import socialShareInit from "@/lib/social-share";
import truncateWrapperInit from "@/lib/truncate-wrapper";
import {
  complementaryProductsInit,
  informationPopupInit,
  inventoryCounterInit,
  optionButtonsInit,
  quantityInputInit,
  reviewsFormatter,
  siblingProductsInit,
  updateBuyButton,
  updatePrices,
  updateSku,
  variantAvailabilityInit,
} from "@/lib/product";
import { cart } from "@/glow";
import { updateUnitPrices } from "@/lib/unit-pricing";
import { wrapIframes, wrapTables } from "@/lib/rte";

const selectors = {
  accordion: ".accordion",
  addToCart: "[data-add-to-cart]",
  socialShareContainer: ".social-share",
  customOptionInputs: "[data-custom-option-input]",
  customOptionInputTargetsById: id => `[data-custom-option-target='${id}']`,
  description: ".product__description",
  displayedDiscount: "[data-discount-display]:not([data-hidden='true'])",
  displayedDiscountByVariantId: id =>
    `[variant-discount-display][variant-id="${id}"] [data-discount-display]`,
  nonSprRatingCountLink: ".product__rating-count-potential-link",
  optionButtons: "[data-option-buttons]",
  optionById: id => `[value='${id}']`,
  optionLabelValue: "[data-selected-value-for-option]",
  priceEls: ".product__price, .product__label-wrapper",
  productBlocksWithPrice:
    ".product__block--product-header, .product__price-block",
  productInventoryJson: "[data-product-inventory-json]",
  productOption: ".product__option",
  productReviews: "#shopify-product-reviews",
  shopPayBanner: "shopify-payment-terms",
  variantSelect: "[data-variant-select]",
};

export default function productBlocks(container, variant = {}) {
  const productBlocksWithPrice = qsa(
    selectors.productBlocksWithPrice,
    container
  );
  const priceEls = productBlocksWithPrice.flatMap(el =>
    qsa(selectors.priceEls, el)
  );
  const displayedDiscountEls = qsa(selectors.displayedDiscount, container);
  const shopPayBannerEls = qsa(selectors.shopPayBanner, container);
  const { productRating } = container.dataset;

  const components = [
    ...accordionElsInit(qsa(selectors.accordion, container)),
    socialShareInit(qs(selectors.socialShareContainer, container)),
    complementaryProductsInit(container),
    informationPopupInit(container),
    quantityInputInit(container),
    siblingProductsInit(container),
    truncateWrapperInit(container),
    variantAvailabilityInit(container), // Handle dynamic variant options
  ];

  const events = [
    ...customOptionsInit(
      qsa(selectors.customOptionInputs, container),
      container
    ), // Adds listeners for each custom option, to sync input changes
    ...optionButtonsInit(qsa(selectors.optionButtons, container)),
  ];

  // Initialize Stock level indicator
  const inventoryCounter = inventoryCounterElInit(
    qs(selectors.productInventoryJson, container),
    container,
    variant
  );

  // Format iframes and tables in description
  formatDescription(qsa(selectors.description, container));

  // Format Shopify Product Reviews
  reviewsFormatter(qsa(selectors.productReviews, document), productRating);

  // Format non-SPR rating display
  formatNonSprRatingCount(qsa(selectors.nonSprRatingCountLink, container));

  const update = ({ variant, srcElement }) => {
    const buyButtonEls = qsa(selectors.addToCart, container);
    const optionParentWrapper = srcElement.closest(selectors.productOption);
    const optionLabel = qs(selectors.optionLabelValue, optionParentWrapper);
    const selectedVariantOpt = qs(
      `${selectors.variantSelect} ${selectors.optionById(variant?.id)}`,
      container
    );

    // Update buy button
    buyButtonEls.forEach(buyButton => {
      updateBuyButton(buyButton, variant);
    });

    // Update prices to reflect selected variant
    updatePrices(productBlocksWithPrice, variant);

    // Hide price and label if variant is unavailable
    priceEls.forEach(el => toggle(el, "hide", !variant));

    // Update unit pricing
    updateUnitPrices(container, variant);

    // Update sku
    updateSku(container, variant);

    // Update displayed discount
    updateDisplayedDiscountEls(container, variant, displayedDiscountEls);

    // Update Stock level indicator
    inventoryCounter && inventoryCounter.update(variant);

    // Update option label
    if (optionLabel) {
      optionLabel.textContent = srcElement.value;
    }

    // Update Shop Pay banners
    shopPayBannerEls.forEach(banner =>
      banner.setAttribute("variant-id", `${variant.id}`)
    );

    if (variant) {
      // We need to set the id input manually so the Dynamic Checkout Button works
      selectedVariantOpt.selected = true;

      // Update container's current variant ID
      container.dataset.currentProductId = variant.id;

      // Update quantity selectors' counts
      cart.get().then(({ items }) => {
        cart.updateQuantitySelectorCounts(null, items, variant.id);
      });
    }
  };

  const unload = () => {
    components.filter(Boolean).forEach(component => component.unload());
    events.forEach(unsubscribe => unsubscribe());
  };

  return { update, unload };
}

const accordionElsInit = els => {
  const accordions = els.map(el => {
    const accordionOpen = el.classList.contains("accordion--open");
    const accordionParent = el.parentElement;
    const accordion = accordionsInit(el, { firstOpen: accordionOpen });

    if (
      accordionParent.classList.contains("rte--product") &&
      !accordionParent.classList.contains("accordion accordion--product")
    ) {
      el.classList.add("rte--product", "accordion--product");
    }

    return accordion;
  });
  return accordions;
};

const customOptionsInit = (els, container) => {
  const listeners = els.map(input => {
    const id = input.dataset.customOptionInput;
    const target = qs(selectors.customOptionInputTargetsById(id), container);
    const listener = listen(input, "change", e => {
      // Update the hidden input within the form, per type
      if (e.target.type === "checkbox") {
        target.checked = e.target.checked;
      } else {
        target.value = e.target.value;
      }
    });
    return listener;
  });
  return listeners;
};

const formatDescription = els => {
  if (!els.length) return;

  els.forEach(el => {
    wrapIframes(qsa("iframe", el));
    wrapTables(qsa("table", el));
  });
};

const formatNonSprRatingCount = els => {
  if (els.length && !qs(selectors.productReviews, document)) {
    // The rating count links to "#shopify-product-reviews" but
    // if that block doesn't exist we should remove the link
    els.forEach(el => el.removeAttribute("href"));
  }
};

const inventoryCounterElInit = (el, container, variant) => {
  if (!el || !variant) return;

  const jsonData = JSON.parse(el.innerHTML);
  const variantsInventories = jsonData.inventory;
  let inventoryCounter;

  if (variantsInventories) {
    const config = {
      id: variant.id,
      variantsInventories,
    };
    inventoryCounter = inventoryCounterInit(container, config);
  }

  return inventoryCounter;
};

const updateDisplayedDiscountEls = (container, variant, els) => {
  if (els.length) {
    els.forEach(discountEl => {
      let newDiscountEl = null;
      let hasDiscount = false;

      if (variant) {
        newDiscountEl = qs(
          selectors.displayedDiscountByVariantId(variant.id),
          container
        );

        if (newDiscountEl) {
          const newDiscountText = newDiscountEl.children[0].textContent;

          if (newDiscountText !== "") {
            hasDiscount = true;
            discountEl.dataset.hasDiscount = "true";
            discountEl.children[0].textContent = newDiscountText;
          }
        }
      }

      if (!hasDiscount) {
        discountEl.dataset.hasDiscount = "false";
        discountEl.children[0].textContent = "";
      }
    });
  }
};
